/**
 * Loads a server-rendered table fragment (JSON {html, meta}) and swaps it in place.
 * State (page, sort, filters, perPage) lives in the URL when syncUrl is on, otherwise in localStorage.
 * Dispatches "table-loaded" on its root element after every load and listens for
 * the window event "refresh-table" to reload the current view without a spinner.
 */
export default function tableLoader({
    id,
    url,
    sort = null,
    direction = 'asc',
    filters = {},
    persist = true,
    syncUrl = false,
    perPage = null,
}) {
    return {
        tableHtml: '',
        meta: {},
        page: 1,
        sort,
        direction,
        filters: {...filters},
        perPage,
        isLoading: false,
        storageKey: `table-state-${id}`,
        _requestId: 0,

        init() {
            if (syncUrl) {
                this.readUrl();
            } else if (persist) {
                this.loadState();
            }

            this.loadTable();

            window.addEventListener('refresh-table', () => this.refresh());

            if (syncUrl) {
                window.addEventListener('popstate', () => {
                    this.readUrl();
                    this.loadTable();
                });
            }
        },

        params() {
            const params = new URLSearchParams();
            params.set('page', this.page);

            if (this.sort) {
                params.set('sort', (this.direction === 'desc' ? '-' : '') + this.sort);
            }

            Object.entries(this.filters).forEach(([key, value]) => {
                if (value !== '' && value !== null && value !== undefined) {
                    params.set(`filter[${key}]`, value);
                }
            });

            if (this.perPage) {
                params.set('per_page', this.perPage);
            }

            return params;
        },

        async loadTable({quiet = false} = {}) {
            const requestId = ++this._requestId;

            if (!quiet) {
                this.tableHtml = '';
                this.isLoading = true;
            }

            try {
                const response = await fetch(`${url}?${this.params()}`, {headers: {Accept: 'application/json'}});

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();

                if (requestId !== this._requestId) {
                    return;
                }

                this.tableHtml = data.html;
                this.meta = data.meta ?? {};

                if (persist && !syncUrl) {
                    this.saveState();
                }

                this.$dispatch('table-loaded', {id, meta: this.meta});
            } catch (error) {
                console.error('Table loader:', error);
            } finally {
                if (requestId === this._requestId) {
                    this.isLoading = false;
                }
            }
        },

        sortBy(column) {
            if (this.sort === column) {
                this.direction = this.direction === 'asc' ? 'desc' : 'asc';
            } else {
                this.sort = column;
                this.direction = 'asc';
            }

            this.page = 1;
            this.navigate();
        },

        goToPage(page) {
            this.page = page;
            this.navigate();
        },

        setPerPage(value) {
            this.perPage = parseInt(value, 10) || perPage;
            this.page = 1;
            this.navigate();
        },

        setFilter(key, value) {
            if (value === '' || value === null || value === undefined) {
                delete this.filters[key];
            } else {
                this.filters[key] = value;
            }

            this.page = 1;
            this.navigate();
        },

        navigate() {
            if (syncUrl) {
                this.writeUrl();
            }

            this.loadTable();
        },

        refresh() {
            this.loadTable({quiet: true});
        },

        updateRow(rowId, html) {
            const row = document.getElementById(`${rowId}`);
            if (row) row.outerHTML = html;
        },

        readUrl() {
            const params = new URLSearchParams(window.location.search);

            this.page = parseInt(params.get('page') ?? '1', 10) || 1;
            this.perPage = parseInt(params.get('per_page'), 10) || perPage;

            const sortParam = params.get('sort');
            if (sortParam) {
                this.direction = sortParam.startsWith('-') ? 'desc' : 'asc';
                this.sort = sortParam.replace(/^-/, '');
            }

            this.filters = {...filters};
            for (const [key, value] of params.entries()) {
                const match = key.match(/^filter\[(.+)\]$/);
                if (match) this.filters[match[1]] = value;
            }
        },

        writeUrl() {
            const target = new URL(window.location);
            const ours = this.params();
            if (this.page === 1) ours.delete('page');
            if (this.perPage === perPage) ours.delete('per_page');

            const merged = new URLSearchParams(window.location.search);
            [...merged.keys()]
                .filter(key => key === 'page' || key === 'sort' || key === 'per_page' || key.startsWith('filter['))
                .forEach(key => merged.delete(key));
            ours.forEach((value, key) => merged.set(key, value));

            target.search = merged.toString();
            window.history.pushState({}, '', target);
        },

        saveState() {
            localStorage.setItem(this.storageKey, JSON.stringify({
                page: this.page,
                sort: this.sort,
                direction: this.direction,
                filters: this.filters,
                perPage: this.perPage,
            }));
        },

        loadState() {
            try {
                const state = JSON.parse(localStorage.getItem(this.storageKey));
                if (state) {
                    this.page = state.page || 1;
                    this.sort = state.sort || sort;
                    this.direction = state.direction || direction;
                    this.filters = state.filters || {...filters};
                    this.perPage = state.perPage || perPage;
                }
            } catch (e) {
            }
        },
    };
}
